import React from 'react';
import { WeekPickerSheet as KitWeekPickerSheet } from '@parsina/framework';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import { useWeekCounts } from '../../hooks/useReception';
import { windowsOn, mergeWindows, hoursRange } from '../../screens/Reception/Reception.shared';
import { useReceptionStrings } from '../../lib/receptionStrings';

// ─────────────────────────────────────────────────────────────────────────────
// Week browsing, moved out of the header into a sheet (DOCTOR_MOBILE_RECEPTION_
// REDESIGN §4.4). The sheet is the kit's `WeekPickerSheet`, shared with the
// clinic's phone پذیرش (PM-0052); the doctor's version adds, per day, WHERE the
// presence schedule puts them. A day with no shift is muted but still
// tappable: appointments can exist outside a shift.
// ─────────────────────────────────────────────────────────────────────────────

const WeekPickerSheet = ({ open, onClose, selISO, todayISO, onPick, presence, clinics = [] }) => {
  const { t, formatDate, formatWeekday, formatDigits, formatNumber } = useTranslation();
  const rs = useReceptionStrings();

  const memberIds = React.useMemo(() => new Set(clinics.map((c) => c.id)), [clinics]);
  const nameOf = (id) => clinics.find((c) => c.id === id)?.name || t('common.labels.clinic');
  const presenceReady = presence?.status === 'ready';

  const describeDay = (iso) => {
    if (!presenceReady) return null;
    const shifts = mergeWindows(windowsOn(presence.windows, iso, memberIds));
    const first = shifts[0];
    if (!first) return { text: '—', off: true };
    const others = new Set(shifts.map((w) => w.clinicId)).size - 1;
    const line = `${nameOf(first.clinicId)} ${hoursRange(first.start, first.end, formatDigits)}`;
    return { text: others > 0 ? `${line} +${formatNumber(others)}` : line, aria: line };
  };

  const fmt = React.useMemo(() => ({
    weekday: (ms) => formatWeekday(ms, 'long'),
    dayMonth: (ms) => formatDate(ms, 'dayMonth'),
    weekdayDayMonth: (ms) => formatDate(ms, 'weekdayDayMonth'),
    number: formatDigits,
  }), [formatWeekday, formatDate, formatDigits]);

  return (
    <KitWeekPickerSheet
      open={open}
      onClose={onClose}
      selISO={selISO}
      todayISO={todayISO}
      onPick={onPick}
      useWeekCounts={useWeekCounts}
      describeDay={describeDay}
      fmt={fmt}
      labels={{
        title: rs('dashboard.week.title'),
        thisWeek: rs('dashboard.week.thisWeek'),
        nextWeek: rs('dashboard.week.nextWeek'),
        prevWeek: rs('dashboard.week.prevWeek'),
        fullCalendar: rs('dashboard.week.fullCalendar'),
        backToWeek: rs('dashboard.week.backToWeek'),
        today: t('dashboard.date.today'),
        close: t('common.actions.close'),
        range: (from, to) => rs('dashboard.week.range', { from, to }),
        countAria: (count) => rs('dashboard.week.countAria', { count }),
      }}
    />
  );
};

export default WeekPickerSheet;
